/**
 * ADR-210 — the client for the page behind a permanent file link.
 *
 * `GET /api/files/resolve?scope=&path=` runs inside Mission Control's session
 * group, so it is called with the SPA's Bearer token like every other API call.
 * It never throws on a 4xx: the server answers a person-caused problem (the file
 * moved, the role may not open it) with `{error, message}` and the page shows
 * that message verbatim.
 */
import { authed } from './pluginFiles'

export interface ResolvedFile {
  exists: true
  scope: string
  path: string
  name: string
  size: number
  modified_at?: string
  media_type: string
  /** Site-relative permanent path, `/files/<scope>/<path>` with each segment encoded. */
  permanent_path: string
  /** Absent for a type the browser may not render inline. 15-minute signed URL. */
  signed_url?: string
  download_url: string
  expires_in_s: number
}

export type ResolveResult =
  | { kind: 'ok'; file: ResolvedFile }
  | { kind: 'not_found'; message: string }
  | { kind: 'error'; status: number; error: string; message: string }

/** What the browser can show for a media type. */
export type ViewerKind = 'video' | 'image' | 'audio' | 'download'

export function viewerFor(file: Pick<ResolvedFile, 'media_type' | 'signed_url'>): ViewerKind {
  if (!file.signed_url) return 'download'
  if (file.media_type.startsWith('video/')) return 'video'
  if (file.media_type.startsWith('image/')) return 'image'
  if (file.media_type.startsWith('audio/')) return 'audio'
  return 'download'
}

export async function resolveFileLink(scope: string, path: string): Promise<ResolveResult> {
  const q = new URLSearchParams({ scope, path })
  let resp: Response
  try {
    resp = await authed(`/api/files/resolve?${q.toString()}`, { method: 'GET' })
  } catch {
    return { kind: 'error', status: 0, error: 'network', message: 'Mission Control could not be reached. Check your connection and try again.' }
  }
  let body: Record<string, unknown> = {}
  try {
    body = (await resp.json()) as Record<string, unknown>
  } catch {
    body = {}
  }
  if (resp.ok) return { kind: 'ok', file: body as unknown as ResolvedFile }
  const message = typeof body.message === 'string' ? body.message : ''
  if (resp.status === 404 && body.error === 'not_found') return { kind: 'not_found', message }
  return {
    kind: 'error',
    status: resp.status,
    error: typeof body.error === 'string' ? body.error : `http_${resp.status}`,
    message:
      message ||
      (resp.status === 401
        ? 'Your session has expired. Please sign in again.'
        : resp.status === 403
          ? 'You do not have permission to open this file.'
          : 'Something went wrong. Please try again.'),
  }
}

/** The absolute permanent link for this Mission Control host. */
export function absoluteLink(permanentPath: string): string {
  return `${window.location.origin}${permanentPath}`
}

export interface CopyResult {
  copied: boolean
  url: string
  reason?: string
}

/**
 * Write `url` to the clipboard. Never throws: a refusal (no user activation, no
 * permission, an insecure context) comes back as `{copied:false, reason}` so the
 * caller can show the link in a selectable box instead.
 */
export async function copyToClipboard(url: string): Promise<CopyResult> {
  const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined
  if (!clip || typeof clip.writeText !== 'function') {
    return { copied: false, url, reason: 'unsupported' }
  }
  try {
    await clip.writeText(url)
    return { copied: true, url }
  } catch (err) {
    const name = err instanceof Error ? err.name : ''
    return { copied: false, url, reason: name === 'NotAllowedError' ? 'not_allowed' : 'failed' }
  }
}
